import { styled, useTheme } from '@mui/material/styles';
import { ReactElement, useMemo } from 'react';
import { Typography } from '../Typography/Typography';
import { ToastMessageType, ToastProps } from '../../types';
import { Button, IconButton, Snackbar } from '@mui/material';
import { ReactComponent as CloseIcon } from '../../assets/icons/close-toast.svg';
import { ReactComponent as SuccessIcon } from '../../assets/icons/success-circle.svg';
import { ReactComponent as WarningIcon } from '../../assets/icons/warning-circle.svg';
import { ReactComponent as ErrorIcon } from '../../assets/icons/error-circle.svg';
import { ReactComponent as InfoIcon } from '../../assets/icons/info-circle.svg';

interface ExtendedToastProps extends ToastProps {
  type: ToastMessageType;
  isOpen: boolean;
  handleClose: () => void;
}

interface ContentProps {
  backgroundColor: string;
  borderColor: string;
}

const Wrapper = styled('div')<ContentProps>`
  width: 368px;
  z-index: 1300;
  border-radius: 4px;
  min-height: 56px;
  background-color: ${({ backgroundColor }) => backgroundColor};
  border-left: 4px solid ${({ borderColor }) => borderColor};
  display: flex;
  flex-direction: column;
  padding: 16px 12px;
  box-shadow: 0px 4px 10px 1px rgba(0, 0, 0, 0.05);
`;

const Header = styled('div')`
  display: flex;
  justify-content: space-between;
  gap: 8px;
`;

const Content = styled('div')`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex: 1;
  svg {
    overflow: visible;
  }
`;

const MessageWrapper = styled('div')``;

const CloseIconWrapper = styled('div')`
  margin-bottom: -2px;
`;

const ControlsWrapper = styled('div')`
  display: flex;
  align-items: flex-start;
`;

const ActionWrapper = styled('div')`
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid ${({ theme }) => theme.colors.border.default};
`;

export const ToastMessage = ({
  type,
  title,
  message,
  action,
  actionLabel,
  isOpen,
  handleClose,
}: ExtendedToastProps): ReactElement => {
  const theme = useTheme();
  const { colors } = theme;

  const MESSAGE_TYPES = useMemo(
    () => ({
      [ToastMessageType.success]: {
        background: theme.colors.surfaceStatus.successLight,
        borderColor: theme.colors.iconStatus.success,
        icon: <SuccessIcon />,
        defaultTitle: 'Success Message',
      },
      [ToastMessageType.warning]: {
        background: theme.colors.surfaceStatus.warningLight,
        borderColor: theme.colors.iconStatus.warning,
        icon: <WarningIcon />,
        defaultTitle: 'Warning Message',
      },
      [ToastMessageType.error]: {
        background: theme.colors.surfaceBackground.bg1,
        borderColor: theme.colors.iconStatus.error,
        icon: <ErrorIcon />,
        defaultTitle: 'Error Message',
      },
      [ToastMessageType.info]: {
        background: theme.colors.surfaceStatus.error,
        borderColor: theme.colors.icon.disabled,
        icon: <InfoIcon />,
        defaultTitle: 'Info Message',
      },
    }),
    [theme.colors]
  );
  return (
    <Snackbar
      open={isOpen}
      autoHideDuration={6000}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      style={{ zIndex: 100005 }}
    >
      <Wrapper
        backgroundColor={MESSAGE_TYPES[type].background}
        borderColor={MESSAGE_TYPES[type].borderColor}
      >
        <Header>
          <Content>
            {MESSAGE_TYPES[type].icon}
            <MessageWrapper>
              <Typography variant='h4' color={colors.text.main}>
                {title ?? MESSAGE_TYPES[type].defaultTitle}
              </Typography>
              <Typography variant='body' color={colors.text.secondary}>
                {message}
              </Typography>
            </MessageWrapper>
          </Content>
          <ControlsWrapper>
            <CloseIconWrapper onClick={handleClose}>
              <IconButton style={{ padding: 0, marginTop: '-2px' }}>
                <CloseIcon />
              </IconButton>
            </CloseIconWrapper>
          </ControlsWrapper>
        </Header>
        {action && (
          <ActionWrapper>
            <Button
              variant='text'
              size='small'
              sx={{
                width: '100%',
                justifyContent: 'flex-start',
                minWidth: 'auto',
                padding: '6px 10px',
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '8px',
                color: colors.textAccent.default,
                backgroundColor: colors.surfaceBackground.highlighted,
                ':hover': {
                  backgroundColor: colors.surfaceInteraction.hover,
                },
              }}
              onClick={() => {
                action();
                handleClose();
              }}
            >
              {actionLabel ?? 'Open'}
            </Button>
          </ActionWrapper>
        )}
      </Wrapper>
    </Snackbar>
  );
};
